import React, { useMemo } from 'react';
import MuiList from '@mui/material/List';
import { makeStyles } from 'tss-react/mui';
import { ListProps } from '@mui/material/List';
import {
  Virtuoso,
  ListProps as VirtualListProps,
  ItemContent,
  VirtuosoHandle,
} from 'react-virtuoso';
import { CommonPropsAndTypes } from '../_Types/StandardComponentProps';

interface IProps<T> extends CommonPropsAndTypes.IPaddingProps {
  component?: React.ElementType<ListProps>;
  nested?: boolean;
  virtual?: boolean;
  totalCount?: number;
  children: React.ReactNode;
  item?: ItemContent<T, unknown>;
  dense?: boolean;
  subheader?: React.ReactElement<any>;
  style?: React.CSSProperties;
  className?: string;
  noPadding?: boolean;
  inCard?: boolean;
  data?: T[];
  testId?: string;
  initialTopMostItemIndex?: number;
  onRangeChanged?: (range: { startIndex: number; endIndex: number }) => void;
}

const useStyles = makeStyles()(theme => ({
  nested: {
    paddingLeft: theme.spacing(4),
  },
  noVertPadding: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
  },
  backgroundColorPaper: {
    backgroundColor: theme.palette.background.paper,
  },
  listContainer: {
    padding: theme.spacing(0),
  },
}));

const style = { height: '100%', width: '100%' };

const listContainer = (noPadding?: string) =>
  React.forwardRef<HTMLDivElement, VirtualListProps>((props, ref) => {
    return (
      <MuiList
        component="div"
        sx={{
          backgroundColor: 'white',
        }}
        ref={ref}
        style={props.style}
        className={noPadding}
      >
        {props.children}
      </MuiList>
    );
  });

export const EDList: <T>(
  props: IProps<T>,
  ref: React.ForwardedRef<VirtuosoHandle>
) => React.ReactElement<IProps<T>> = (props, ref) => {
  const { classes, cx } = useStyles();
  const {
    children,
    nested,
    disableVerticalPadding,
    testId,
    subheader,
    virtual,
    totalCount,
    noPadding,
    inCard,
    item,
    initialTopMostItemIndex,
    onRangeChanged,
    ...rest
  } = props;
  const container: React.FunctionComponent<
    React.PropsWithChildren<VirtualListProps>
  > = useMemo(
    () => listContainer(noPadding ? classes.listContainer : undefined),
    [classes.listContainer, noPadding]
  );

  const className = cx(props.className, {
    [classes.nested]: nested,
  });
  const useVirtual = props.virtual && totalCount && !inCard;

  return useVirtual && item && totalCount ? (
    <Virtuoso
      totalCount={totalCount}
      itemContent={item}
      data={props.data}
      components={{
        List: container,
      }}
      style={style}
      ref={ref}
      initialTopMostItemIndex={initialTopMostItemIndex ?? 0}
      rangeChanged={onRangeChanged}
    />
  ) : (
    <MuiList
      {...rest}
      data-testid={testId}
      className={className}
      classes={{
        padding: disableVerticalPadding ? classes.noVertPadding : '',
      }}
      subheader={subheader}
    >
      {children}
    </MuiList>
  );
};

export default React.forwardRef(EDList);
